import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Image as ImageIcon, X, StopCircle, Bot, PlusCircle, Sparkles, CheckCircle, ChevronDown, ChevronRight, History, Paperclip, MessageSquare, Leaf } from 'lucide-react';
import ApiService from '../services/apiService';
import TranslationService from '../services/translationService';
import Badge from '../components/ui/Badge';
import MarkdownText from '../components/ui/MarkdownText';
import { commitModule, markRun } from '../utils/reportMarkers';
import { capDataUrl } from '../utils/imageGuards';
import { stripThink } from '../utils/aiParsers';

// ── Chat History Manager (localStorage) ─────────────────────────────────────
const HISTORY_KEY = 'geonutria_chat_history';

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveSession(session) {
  const history = loadHistory();
  // Strip heavy data (base64 images, file data URIs) before persisting
  const lightMessages = (session.messages || []).map(m => ({
    role: m.role,
    content: typeof m.content === 'string' ? m.content : (Array.isArray(m.content) ? m.content.find(c => c.type === 'text')?.text || '' : ''),
    thinking: m.thinking || '',
    fileName: m.fileName || null,
    // Intentionally omit: image, dataUri, apiContent, preview
  }));
  const lightSession = { id: session.id, preview: session.preview, messages: lightMessages, updatedAt: session.updatedAt };
  const idx = history.findIndex(h => h.id === lightSession.id);
  if (idx >= 0) history[idx] = lightSession;
  else history.unshift(lightSession);
  // Keep last 30 sessions
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 30)));
  } catch (e) {
    // If still too large, trim older sessions until it fits
    let trimmed = history.slice(0, 30);
    while (trimmed.length > 1) {
      trimmed.pop();
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed)); return; } catch {}
    }
    // Last resort: clear history entirely
    localStorage.removeItem(HISTORY_KEY);
  }
}

function deleteSession(id) {
  const history = loadHistory().filter(h => h.id !== id);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

// ── Report capture (Advanced AI section) ────────────────────────────────────
// The full transcript lives in localStorage; the report carries only the last few
// question/answer pairs and at most two images. `thinking` NEVER leaves this view.

const ADV_MAX_TURNS = 6;
const ADV_MAX_ATTACHMENTS = 2;
const GLOBAL_CONTEXT_KEY = 'geonutria_global_context';

function readGlobalContext() {
  try {
    const saved = JSON.parse(localStorage.getItem(GLOBAL_CONTEXT_KEY) || '{}');
    return {
      location: saved.location || '',
      season: saved.season || '',
      cropType: saved.cropType || ''
    };
  } catch { return { location: '', season: '', cropType: '' }; }
}

/**
 * Build the report's `advanced_ai` object from a chat transcript.
 * @param {Array} transcript  user/assistant messages, oldest first
 * @param {string} sessionId
 */
async function buildAdvancedAi(transcript, sessionId) {
  const turns = [];
  for (let i = 0; i < transcript.length; i += 1) {
    const msg = transcript[i];
    if (!msg || msg.role !== 'user') continue;
    const next = transcript[i + 1];
    if (!next || next.role !== 'assistant') continue;
    const question = typeof msg.content === 'string' ? msg.content : '';
    const answer = typeof next.content === 'string' ? next.content : '';
    if (!question && !answer) continue;
    turns.push({ question, answer });   // never `thinking`
  }

  // Images the user attached, most recent first past the cap.
  const images = [];
  for (const msg of transcript) {
    if (!msg || msg.role !== 'user') continue;
    const uri = msg.image || msg.apiContent?.[1]?.image_url?.url || null;
    if (typeof uri === 'string' && uri.startsWith('data:image')) {
      images.push({ name: msg.fileName || 'attachment', uri });
    }
  }

  const attachments = [];
  for (const img of images.slice(-ADV_MAX_ATTACHMENTS)) {
    const capped = await capDataUrl(img.uri);
    if (!capped) continue;
    const mime = capped.slice(5).split(';')[0] || 'image/jpeg';
    attachments.push({ name: img.name, mime, image_base64: capped });
  }

  return {
    session_id: `s_${sessionId}`,
    session_date: new Date().toISOString(),
    model: 'openrouter',
    context: readGlobalContext(),
    turns: turns.slice(-ADV_MAX_TURNS),
    attachments
  };
}

// ── Component ───────────────────────────────────────────────────────────────

export default function NewAIView({ theme, isRTL, lang = 'en', setReportData, userId }) {
  const t = TranslationService.getTranslation(lang)?.new_ai || {};

  // ── State ──
  const [sessionId, setSessionId] = useState(() => Date.now().toString());
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);   // { file, preview, type }
  const [isTyping, setIsTyping] = useState(false);
  const [modelOnline, setModelOnline] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState(() => loadHistory());

  // ── Refs ──
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // ── Health check ──
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${ApiService.BASE_URL}/v1/health`);
        setModelOnline(res.ok);
      } catch { setModelOnline(false); }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  // Phase 3: Instant client-side translation fallback
  const previousLangRef = useRef(lang);
  useEffect(() => {
    if (lang && previousLangRef.current !== lang) {
      previousLangRef.current = lang;
      
      const translateMessages = async () => {
        if (isTyping) return; // Don't translate while streaming
        let hasChanges = false;
        const updatedMessages = await Promise.all(messages.map(async (msg) => {
          if (msg.role === 'assistant' && msg.content) {
            const translated = await TranslationService.translateText(msg.content, lang);
            if (translated !== msg.content) hasChanges = true;
            return { ...msg, content: translated };
          }
          return msg;
        }));
        
        if (hasChanges) {
           setMessages(updatedMessages);
        }
      };
      
      translateMessages();
    }
  }, [lang, messages, isTyping]);

  // ── Auto-scroll ──
  const handleScroll = useCallback(() => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      setAutoScroll(scrollHeight - scrollTop - clientHeight < 100);
    }
  }, []);

  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll, isTyping]);

  // ── Persist session ──
  useEffect(() => {
    if (messages.length > 0) {
      const firstUserMsg = messages.find(m => m.role === 'user');
      const preview = firstUserMsg?.content?.slice(0, 60) || 'New chat';
      saveSession({ id: sessionId, preview, messages, updatedAt: Date.now() });
      setChatHistory(loadHistory());
    }
  }, [messages, sessionId]);

  // ── File handling ──
  const handleFileSelect = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachedFile({
        file,
        preview: file.type.startsWith('image/') ? reader.result : null,
        type: file.type,
        name: file.name,
        dataUri: reader.result
      });
    };
    reader.readAsDataURL(file);
  };

  const handleFileInput = (e) => {
    handleFileSelect(e.target.files[0]);
  };

  const removeFile = () => setAttachedFile(null);

  // ── Clipboard paste (Ctrl+V image support) ──
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleFileSelect(file);
        return;
      }
    }
  }, []);

  // ── Controls ──
  const stopResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsTyping(false);
  };

  const clearChat = () => {
    stopResponse();
    setMessages([]);
    setInputValue('');
    removeFile();
    setSessionId(Date.now().toString());
  };

  const loadSession = (session) => {
    stopResponse();
    setSessionId(session.id);
    setMessages(session.messages || []);
    setShowHistory(false);
  };

  // ── Send Message ──
  const sendMessage = async (presetText = null, suggestionPrefix = null) => {
    const textToSend = presetText || inputValue;
    if (!textToSend.trim() && !attachedFile) return;

    // Build user message for display
    const newUserMsg = {
      role: 'user',
      content: textToSend,
      image: attachedFile?.preview || null,
      fileName: attachedFile?.name || null,
    };

    // Build the content payload for OpenRouter
    let userContentPayload;
    if (attachedFile) {
      userContentPayload = [
        { type: 'text', text: suggestionPrefix ? `${suggestionPrefix}\n\n${textToSend}` : textToSend },
        { type: 'image_url', image_url: { url: attachedFile.dataUri } }
      ];
    } else {
      userContentPayload = suggestionPrefix ? `${suggestionPrefix}\n\n${textToSend}` : textToSend;
    }

    // Build full message history for API
    const apiMessages = [
      ...messages.filter(m => m.role).map(m => ({ role: m.role, content: m.apiContent || m.content })),
      { role: 'user', content: userContentPayload }
    ];

    setMessages(prev => [...prev, { ...newUserMsg, apiContent: userContentPayload }]);
    setInputValue('');
    removeFile();
    setIsTyping(true);
    setAutoScroll(true);

    abortControllerRef.current = new AbortController();

    /* Local mirrors of the assistant turn. The `messages` state value is a stale
       closure inside this handler, so the report is built from these instead. */
    let assistantText = '';
    let streamErrorMsg = null;

    /* Snapshot of the transcript BEFORE this turn - `messages` is still the
       pre-send value here, which is exactly what we want. */
    const priorMessages = messages;

    const captureTurn = async (ok, errText) => {
      if (!setReportData) return;
      /* Chain-of-thought must never reach the PDF the farmer receives. */
      const answer = stripThink(assistantText);
      if (!answer.trim()) {
        /* A turn that ran and failed with no visible output must still be
           distinguishable from one that never ran. A silent SUCCESS stays
           unrecorded so an empty section is never activated. */
        if (!ok) markRun(setReportData, 'advanced_ai', false, errText ? String(errText).slice(0, 200) : null);
        return;
      }
      try {
        const advanced = await buildAdvancedAi(
          [...priorMessages, newUserMsg, { role: 'assistant', content: answer }],
          sessionId
        );
        commitModule(setReportData, 'advanced_ai', { advanced_ai: advanced },
          ok, errText ? String(errText).slice(0, 200) : null);
      } catch (captureErr) {
        console.error('Advanced AI report capture failed', captureErr);
      }
    };

    try {
      const response = await fetch(`${ApiService.BASE_URL}/v1/openrouter-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortControllerRef.current.signal,
        body: JSON.stringify({
          messages: apiMessages,
          stream: true,
          lang: lang || (isRTL ? 'ar' : 'en'),
          reasoning: { effort: "high" }
        })
      });

      if (!response.ok) throw new Error(`API Error (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = ''; // Holds incomplete SSE data between network chunks

      setMessages(prev => [...prev, { role: 'assistant', content: '', thinking: '', isThinking: false }]);

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;

        // Prepend any leftover data from the previous chunk
        sseBuffer += decoder.decode(value, { stream: true });

        // Split on double-newline (SSE message boundary)
        const parts = sseBuffer.split('\n\n');
        // The last element may be incomplete — keep it in the buffer
        sseBuffer = parts.pop() || '';

        for (const part of parts) {
          const lines = part.split('\n').filter(l => l.trim());
          for (let line of lines) {
            if (line.startsWith('data: ')) line = line.slice(6);
            if (line === '[DONE]') continue;

            let data;
            try {
              data = JSON.parse(line);
            } catch {
              // Truly malformed line (not a fragment) — skip
              continue;
            }

            if (data.error) {
              const errMsg = data.error.message || data.error || 'Unknown error';
              streamErrorMsg = errMsg;
              setMessages(prev => {
                const n = [...prev];
                n[n.length - 1] = { ...n[n.length - 1], content: `**Error**: ${errMsg}` };
                return n;
              });
              continue;
            }

            let tokenContent = '';
            let tokenReasoning = '';

            const delta = data.choices?.[0]?.delta;
            if (delta) {
              if (delta.content !== undefined) tokenContent = delta.content;
              if (delta.reasoning !== undefined) tokenReasoning = delta.reasoning;
            } else if (data.message?.content !== undefined) {
              tokenContent = data.message.content;
            }

            if (!tokenContent && !tokenReasoning) continue;

            /* Accumulate the content channel RAW and sanitise once at capture
               time with stripThink(). A per-token state machine cannot work
               here: <think> spans arbitrarily many deltas, so any flag cleared
               on a tag-less token leaks the model's reasoning into the report.
               The reasoning channel never contributes to the payload at all. */
            if (!tokenReasoning) {
              assistantText += tokenContent;
            }

            setMessages(prev => {
              const n = [...prev];
              const msg = { ...n[n.length - 1] };

              // Route reasoning tokens to thinking state
              if (tokenReasoning) {
                msg.isThinking = true;
                msg.thinking += tokenReasoning;
                n[n.length - 1] = msg;
                return n;
              }

              // Handle <think>...</think> tags in content stream
              if (tokenContent.includes('<think>')) {
                msg.isThinking = true;
                const parts = tokenContent.split('<think>');
                if (parts[0]) msg.content += parts[0];
                if (parts[1]) msg.thinking += parts[1];
                n[n.length - 1] = msg;
                return n;
              }
              if (msg.isThinking && tokenContent.includes('</think>')) {
                msg.isThinking = false;
                const parts = tokenContent.split('</think>');
                if (parts[0]) msg.thinking += parts[0];
                if (parts[1]) msg.content += parts[1];
                n[n.length - 1] = msg;
                return n;
              }
              if (msg.isThinking && !tokenContent.includes('</think>')) {
                // If we are still in <think> tags, append to thinking. 
                // BUT if we entered thinking via tokenReasoning, we shouldn't be here because tokenReasoning would be truthy. 
                // If tokenReasoning is falsy but we were in isThinking, it means we either:
                // 1. Are inside a <think> block from tokenContent
                // 2. Just transitioned from tokenReasoning to tokenContent (end of reasoning)
                // Let's assume OpenRouter nemotron won't mix <think> and delta.reasoning. 
                // We'll turn off isThinking if we get content but no reasoning (assuming it's a delta.reasoning transition).
                // Actually, let's just turn off isThinking. 
              }

              // Better logic:
              if (msg.isThinking) {
                  // Did we get here via <think> or delta.reasoning?
                  // If we got here via delta.reasoning, then msg.thinking was populated by tokenReasoning.
                  // Now we have tokenContent and NO tokenReasoning.
                  // So thinking is done!
                  msg.isThinking = false;
              }

              msg.content += tokenContent;
              n[n.length - 1] = msg;
              return n;
            });
          }
        }
      }
      // Turn finished - hand the visible answer to the report.
      captureTurn(!streamErrorMsg, streamErrorMsg);

    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => [...prev, { role: 'assistant', content: `**${t.error || 'Error'}**: ${err.message}` }]);
        captureTurn(false, err.message);
      } else {
        // Stopped by the user: whatever streamed is real, just truncated.
        captureTurn(false, 'Response stopped before completion');
      }
    } finally {
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Animation Variants ──
  const msgVariants = {
    hidden: { opacity: 0, y: 16, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 400, damping: 30 } },
    exit: { opacity: 0, y: -8, transition: { duration: 0.15 } }
  };

  const slideVariants = {
    hidden: { x: isRTL ? 280 : -280, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    exit: { x: isRTL ? 280 : -280, opacity: 0, transition: { duration: 0.2 } }
  };

  return (
    <div className="w-full h-full bg-white dark:bg-[#0A0F1E] rounded-[2rem] overflow-hidden border border-[#1B263B]/10 dark:border-white/10 shadow-[0_8px_32px_rgba(27,38,59,0.05)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.2)] flex relative" dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── History Sidebar ── */}
      <AnimatePresence>
        {showHistory && (
          <motion.aside
            variants={slideVariants} initial="hidden" animate="visible" exit="exit"
            className="absolute top-0 bottom-0 ltr:left-0 rtl:right-0 w-72 z-30 bg-white/95 dark:bg-[#0E1324]/95 backdrop-blur-xl border-r dark:border-white/5 border-gray-100 flex flex-col shadow-2xl"
          >
            <div className="p-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#1B263B] dark:text-white">{t.chatHistory}</h3>
              <button onClick={() => setShowHistory(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
              {chatHistory.length === 0 ? (
                <p className="text-center text-xs text-gray-400 dark:text-gray-600 py-8">{t.noHistory}</p>
              ) : (
                chatHistory.map(session => (
                  <motion.button
                    key={session.id}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => loadSession(session)}
                    className={`w-full text-left rtl:text-right p-3 rounded-xl text-sm transition-all group ${session.id === sessionId ? 'bg-[#C47A2C]/10 text-[#C47A2C] font-semibold' : 'hover:bg-gray-50 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400'}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <MessageSquare size={14} className="shrink-0 opacity-50" />
                      <span className="truncate flex-1">{session.preview}</span>
                    </div>
                  </motion.button>
                ))
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ── Main Chat Column ── */}
      <div className="flex-1 flex flex-col h-full min-w-0">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-white/5 bg-white/80 dark:bg-[#0A0F1E]/80 backdrop-blur-xl z-10 shrink-0"
        >
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 rounded-xl bg-gray-100/80 dark:bg-white/5 hover:bg-[#C47A2C]/10 hover:text-[#C47A2C] text-gray-500 dark:text-gray-400 transition-all"
            >
              <History size={18} />
            </motion.button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C47A2C] to-[#6B8F71] flex items-center justify-center text-white shadow-lg shadow-[#C47A2C]/20">
              <Leaf size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#1B263B] dark:text-white leading-tight">{t.title}</h2>
              <p className="text-[0.65rem] text-[#6B8F71] dark:text-[#88b58f] font-semibold tracking-wider uppercase">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Badge type={modelOnline ? 'success' : 'danger'}>
              <span className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${modelOnline ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
                {modelOnline ? t.online : t.offline}
              </span>
            </Badge>
            <motion.button
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={clearChat}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100/80 dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:bg-[#C47A2C]/10 hover:text-[#C47A2C] transition-all text-xs font-semibold"
            >
              <PlusCircle size={14} />
              {t.newChat}
            </motion.button>
          </div>
        </motion.div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-5 custom-scrollbar bg-[#FAFAF8] dark:bg-[#070B17]" ref={chatContainerRef} onScroll={handleScroll}>
          {messages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, type: 'spring' }}
              className="flex flex-col items-center justify-center h-full text-center"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#C47A2C]/15 to-[#6B8F71]/15 flex items-center justify-center mb-6"
              >
                <Leaf size={36} className="text-[#C47A2C]" />
              </motion.div>
              <h3 className="text-xl font-bold text-[#1B263B] dark:text-white mb-2">{t.welcomeTitle}</h3>
              <p className="max-w-md text-sm text-gray-400 dark:text-gray-500 leading-relaxed">{t.welcomeDesc}</p>
            </motion.div>
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx} variants={msgVariants} initial="hidden" animate="visible" exit="exit" layout
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] lg:max-w-[75%] rounded-2xl p-4 transition-shadow duration-300 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 text-[#065F46] dark:text-emerald-100 rounded-tr-sm shadow-sm hover:shadow-md'
                      : 'bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] text-[#1B263B] dark:text-gray-200 rounded-tl-sm shadow-sm hover:shadow-md'
                  }`}>
                    {/* User image */}
                    {msg.image && (
                      <img src={msg.image} alt="Upload" className="max-w-xs rounded-xl mb-3 shadow-md" />
                    )}
                    {msg.fileName && !msg.image && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-gray-100 dark:bg-white/5 rounded-lg text-xs font-medium">
                        <Paperclip size={12} /> {msg.fileName}
                      </div>
                    )}

                    {/* Thinking accordion */}
                    {msg.role === 'assistant' && (msg.thinking || msg.isThinking) && (
                      <ThinkingAccordion thinking={msg.thinking} isLive={msg.isThinking} t={t} />
                    )}

                    {/* Content */}
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      {msg.role === 'assistant' ? (
                        <MarkdownText text={msg.content} />
                      ) : (
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {/* Typing dots */}
          <AnimatePresence>
            {isTyping && messages.length > 0 && !messages[messages.length - 1]?.content && !messages[messages.length - 1]?.thinking && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                className="flex justify-start"
              >
                <div className="bg-white dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] rounded-2xl rounded-tl-sm px-5 py-3.5 flex items-center gap-1.5">
                  {[0, 1, 2].map(i => (
                    <motion.span key={i}
                      animate={{ y: [0, -6, 0] }}
                      transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}
                      className="w-2 h-2 rounded-full bg-[#C47A2C]"
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3 md:p-4 bg-white dark:bg-[#0A0F1E] border-t border-gray-100 dark:border-white/[0.06] shrink-0">

          {/* Suggestion pills */}
          <AnimatePresence>
            {messages.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                className="flex flex-wrap gap-2 mb-3 px-1"
              >
                {[
                  { icon: CheckCircle, label: t.exportCompliance, desc: t.exportComplianceDesc, prefix: '[SYSTEM INSTRUCTION: Focus response on European Export Compliance standards]' },
                  { icon: Sparkles, label: t.deepDiagnosis, desc: t.deepDiagnosisDesc, prefix: '[SYSTEM INSTRUCTION: Perform a deep-dive diagnosis]' },
                ].map((s, i) => (
                  <motion.button
                    key={i} whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }}
                    onClick={() => sendMessage(s.desc, s.prefix)}
                    className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-[#1B263B]/[0.03] to-[#C47A2C]/[0.03] dark:from-white/[0.03] dark:to-[#C47A2C]/[0.05] hover:from-[#C47A2C]/10 hover:to-[#C47A2C]/5 text-gray-600 dark:text-gray-400 hover:text-[#C47A2C] border border-gray-100 dark:border-white/[0.06] hover:border-[#C47A2C]/30 text-xs font-semibold rounded-xl transition-all"
                  >
                    <s.icon size={14} /> {s.label}
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* File preview */}
          <AnimatePresence>
            {attachedFile && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="mb-2.5 overflow-hidden"
              >
                <div className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-xl w-fit">
                  {attachedFile.preview ? (
                    <img src={attachedFile.preview} alt="Preview" className="w-12 h-12 object-cover rounded-lg shadow-sm" />
                  ) : (
                    <div className="w-12 h-12 bg-[#C47A2C]/10 rounded-lg flex items-center justify-center">
                      <Paperclip size={18} className="text-[#C47A2C]" />
                    </div>
                  )}
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 max-w-[160px] truncate">{attachedFile.name}</span>
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={removeFile} className="p-1.5 bg-rose-100 dark:bg-rose-500/10 text-rose-500 rounded-full hover:bg-rose-200 transition-colors">
                    <X size={12} />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input bar */}
          <div className="flex items-end gap-2 bg-gray-50/80 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-2xl p-1.5 focus-within:border-[#C47A2C]/50 focus-within:ring-2 focus-within:ring-[#C47A2C]/10 transition-all">
            <motion.label
              whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
              className="p-2.5 text-gray-400 hover:text-[#C47A2C] cursor-pointer transition-colors shrink-0 rounded-xl hover:bg-[#C47A2C]/5"
            >
              <Paperclip size={18} />
              <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv" className="hidden" onChange={handleFileInput} />
            </motion.label>

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={t.placeholder}
              className="flex-1 bg-transparent border-none focus:ring-0 focus:outline-none resize-none max-h-32 min-h-[40px] py-2.5 px-1 text-sm text-[#1B263B] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600"
              rows={1}
            />

            {isTyping ? (
              <motion.button
                whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
                onClick={stopResponse}
                title={t.stop}
                className="p-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl transition-all shrink-0 shadow-lg shadow-rose-500/20"
              >
                <StopCircle size={18} />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() && !attachedFile}
                className="p-2.5 bg-gradient-to-r from-[#C47A2C] to-[#A36322] hover:shadow-lg hover:shadow-[#C47A2C]/25 disabled:opacity-30 disabled:grayscale disabled:shadow-none text-white rounded-xl transition-all shrink-0"
              >
                <Send size={18} className={isRTL ? 'rotate-180' : ''} />
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Thinking Accordion Sub-Component ────────────────────────────────────────

function ThinkingAccordion({ thinking, isLive, t }) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-expand while actively thinking
  useEffect(() => {
    if (isLive) {
      setExpanded(true);
      setAutoScroll(true);
    }
  }, [isLive]);

  // Smooth auto-scroll with delay
  useEffect(() => {
    if (isLive && expanded && autoScroll && scrollRef.current) {
      const timeoutId = setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [thinking, isLive, expanded, autoScroll]);

  // Detect manual scroll up
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 15;
    setAutoScroll(isAtBottom);
  };

  return (
    <div className="mb-3">
      <motion.button
        whileHover={{ scale: 1.01 }}
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[#C47A2C]/[0.06] dark:bg-[#C47A2C]/[0.08] rounded-xl text-xs font-bold text-[#C47A2C] uppercase tracking-wider select-none transition-colors hover:bg-[#C47A2C]/10"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronRight size={14} />
        </motion.div>
        <Sparkles size={12} />
        {isLive ? t.thinkingLive || 'Reasoning…' : t.thinking || 'Thinking Process'}
        {isLive && (
          <motion.span
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 rounded-full bg-[#C47A2C] ltr:ml-auto rtl:mr-auto"
          />
        )}
      </motion.button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className="mt-2 ltr:pl-5 ltr:border-l-2 rtl:pr-5 rtl:border-r-2 border-[#C47A2C]/20 text-xs text-gray-500 dark:text-gray-400 italic whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto custom-scrollbar py-1"
            >
              {thinking}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
